import React, { useState, useEffect, useRef } from 'react';
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import Text from '@directv-digital/fusion.atoms.text'
import CardMedia from "@mui/material/CardMedia";
import { CloseIcon } from "@directv-digital/fusion.atoms.icons";
import DtvTheme from "@directv-digital/dds.ui.dtv-theme";
import CustomChip from './components/customChip';
import Button from '@directv-digital/fusion.atoms.button';
import Alert from '@directv-digital/fusion.molecules.alert';
import { useComparePkgTrayContext } from './ComparePkgTrayContext/useComparePkgTrayContext';
import { absoluteLinkUrl, fireLinkClickFromAttrs, getLinkClickAttrs, usePageUrl } from 'src/utils/link-click';
import { linkNameOnPress } from 'src/utils/press-link-name';
import { ComparePackageTrayStyles } from './ComparePackageTray.styles';
import { ComparePkgTray } from './ComparePackageTray.props';
import { INERT_SLOT } from './inertSlot';

const styles = ComparePackageTrayStyles();
/** WAT-12478 — linkPosition values, unchanged from the old JS pushes. */
const TRAY_POSITION = 'comparison_tray';
const CHIP_POSITION = 'comparison_tray_modal';

const ComparePackagesTrayModule = (props: ComparePkgTray) => {
  const { title, subTitle, subHeading1, subHeading2, subHeading3, maxSelectedPackages, defaultPackages, cta1, cta2, alert, enableSectionToggle } = props;
  const [showSignaturePacks, setShowSignaturePacks] = useState(false);
  const { trayOpen, selectedItems, isMaxSelected, updateSelectedTag, collapsedTray, updateCollapsedTray, setTrayOpen, maxSelectedTags } = useComparePkgTrayContext();
  // When the tray is collapsed the chips container is kept mounted (opacity 0) for the slide
  // animation. aria-hidden alone left its chip links / toggles in the focus order, which fails
  // the accessibility-tree audit ("aria-hidden element must not contain focusable elements").
  // `inert` takes it out of focus order AND the a11y tree; INERT_SLOT spells the React 18/19 difference.
  const chipsHidden = !(trayOpen && collapsedTray);
  const hadSelection = useRef(false);
  // WAT-12478 — GTM auto-capture reports every click below from the governance
  // attributes (src/utils/link-click.ts); nothing is pushed on click. The
  // div-based role="button"/checkbox controls get no click from Enter/Space, so
  // only their keyboard path calls fireLinkClick (AC10).
  const pageUrl = usePageUrl();
  // AC6 — the chip's visible package name, with today's _selected / _unselected outcome suffix.
  const getChipName = pack => `${pack?.packageName || pack?.queryparam}${selectedItems?.includes(pack?.queryparam) ? '_unselected' : '_selected'}`;
  const toggleName = collapsedTray ? 'Collapse compare packages tray' : 'Expand compare packages tray';
  // Attributes are passed as explicit props (not spread) so static analysis can see every
  // key is a non-executable aria-label / data-* attribute.
  const toggleAttrs = getLinkClickAttrs(toggleName, TRAY_POSITION, pageUrl);
  const closeAttrs = getLinkClickAttrs('Close compare packages tray', TRAY_POSITION, pageUrl);
  const fireToggleKeyboard = () => fireLinkClickFromAttrs(toggleAttrs);
  // The title text toggles too: same name as the arrow, written for the press only (the
  // container has no role, so a static aria-label would be meaningless to AT).
  const titlePress = linkNameOnPress(selectedItems?.length > 0 ? toggleAttrs : null);
  // The arrow / close buttons sit inside the clickable title container. They no longer
  // stopPropagation (that hid their clicks from GTM's document listener), so the container
  // ignores events that come from a nested role="button" instead of toggling twice.
  const fromNestedButton = (e: React.SyntheticEvent) => {
    const pressed = (e.target as Element)?.closest?.('[role="button"]');
    return !!pressed && pressed !== e.currentTarget && e.currentTarget.contains(pressed);
  };

  useEffect(() => {
    if (!enableSectionToggle) {
      return;
    }
    const selected = selectedItems || [];
    if (selected.length === 0) {
      hadSelection.current = false;
      setShowSignaturePacks(false);
      return;
    }
    if (!hadSelection.current) {
      hadSelection.current = true;
      const genreQueryparams = [...(subHeading1?.chips || []), ...(subHeading3?.chips || [])]
        .map((chip: any) => chip?.queryparam)
        .filter(Boolean);
      if (genreQueryparams.length > 0 && !selected.some((qp: string) => genreQueryparams.includes(qp))) {
        setShowSignaturePacks(true);
      }
    }
  }, [enableSectionToggle, selectedItems, subHeading1, subHeading3]);
  const getCustomChips = data => (data.map((pack, index) => (
                <CustomChip
                    key={index}
                    pkgName={pack?.packageName}
                    price={pack?.price}
                    channels={pack?.channels}
                    isSelected={selectedItems.includes(pack?.queryparam)}
                    onClick={() => handleSelectedTag(pack?.queryparam)}
                    linkClickAttrs={getLinkClickAttrs(getChipName(pack), CHIP_POSITION, pageUrl)}
                    onKeyboardActivate={() => fireLinkClickFromAttrs(getLinkClickAttrs(getChipName(pack), CHIP_POSITION, pageUrl))}
                    type={pack?.subType || pack?.type}
                />
  )))
  const handleClearAll = () => {
    updateSelectedTag('');
  }
  const getCompareUrl = url => {
    if (url === '' || !selectedItems?.length) {
      return '';
    }
    const defaults = Array.isArray(defaultPackages) ?
      defaultPackages.map(p => p?.toLowerCase()).filter(Boolean) :
      [];
    const merged = [...new Set([...selectedItems.map(s => s?.toLowerCase()), ...defaults])].slice(0, maxSelectedTags || 3);
    return `${url}${merged.join(',')}`;
  }
  const cta1Attrs = getLinkClickAttrs(cta1?.content, TRAY_POSITION, absoluteLinkUrl(getCompareUrl(cta1?.targetUrl), pageUrl));
  const cta2Attrs = getLinkClickAttrs(cta2?.content, TRAY_POSITION, pageUrl);
  const handleCompareClick = url => {
    const targetUrl = getCompareUrl(url);
    if (targetUrl) {
      window.location.href = targetUrl;
    }
  }
  const handleSelectedTag = tag => {
    updateSelectedTag(tag);
  };
  const handleCollapsedTray = () => {
    updateCollapsedTray(!collapsedTray)
  }
  const handleTray = () => {
    setTrayOpen(false);
    updateCollapsedTray(false);
  }
  const getsubHeading3 = () => (
            <DtvBox
                id="compare-package-multipack-container"
                sx={{
                    display: 'flex',
                    justifyContent: 'start',
                    flexDirection: 'column',
                    gap: "5px"
                }}
            >
                <DtvBox
                    id="compare-package-multipack-text-container"
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '7px',
                        alignItems: 'center'
                    }}
                >
                    <Text
                        variant='body1'
                        aria-label={subHeading3?.content}
                        sx={{
                            ...styles.subHeading1TitleText,
                            fontSize: '16px !important'
                        }}
                    >
                        {subHeading3?.content}
                    </Text>
                    {subHeading3?.offerRibbon?.content && <Text
                        variant='body1'
                        aria-label={subHeading3?.offerRibbon?.content}
                        sx={{
                            p: "4px 8px",
                            fontSize: "12px !important",
                            width: "fit-content",
                            height: "fit-content",
                            gap: "10px",
                            borderRadius: "2px",
                            backgroundColor: subHeading3?.offerRibbon?.background
                        }}
                    >
                        {subHeading3?.offerRibbon?.content}
                    </Text>}
                </DtvBox>
                <DtvBox
                    id="compare-package-multipack-chip-container"
                    sx={{
                        ...styles.genrePacksChipsContainer
                    }}
                >
                        {subHeading3?.chips && getCustomChips(subHeading3?.chips)}
                </DtvBox>
            </DtvBox>)
  try {
    return (
            <DtvBox
                id="compare-packages-container"
                sx={{
                    ...styles.container,
                    display: 'flex',
flexDirection: 'column',
gap: '5px',
                    transform: (trayOpen && collapsedTray) ? 'translateY(0%)' : !trayOpen ? 'translateY(100%)' : 'translateY(85%)',
                    transition: 'transform 0.3s ease-in'
                }}
            >
                <DtvBox
                    sx={{
                        ...styles.titleContainer
                    }}
                    // A mouse convenience only: no role / static aria-label / tab stop, so AT and
                    // keyboard users get the one arrow button inside. Its data-* let GTM report a
                    // click on the title text through the nearest-ancestor rule, named with the
                    // arrow's toggle name written at press time; a click on the arrow is reported
                    // by the arrow itself, so each click is reported once, with one name.
                    data-linkposition={selectedItems?.length > 0 ? toggleAttrs['data-linkposition'] : undefined}
                    data-linkdestination={selectedItems?.length > 0 ? toggleAttrs['data-linkdestination'] : undefined}
                    onPointerDownCapture={event => {
                        if (!fromNestedButton(event)) {
                            titlePress.onPointerDownCapture(event);
                        }
                    }}
                    onClick={event => {
                        if (fromNestedButton(event)) {
                            return;
                        }
                        selectedItems?.length > 0 && handleCollapsedTray()
                    }}
                >
                    <DtvBox
                        sx={{
                            ...styles.titleBox,
                            ...(!collapsedTray && {
                                paddingBottom: '14px'
                            })
                        }}
                    >
                        <Text
                            variant='body2'
                            aria-label={collapsedTray ? title?.content : subTitle?.content}
                            sx={{
                              ...styles.titleText
                            }}
                        >{collapsedTray ? title?.content : subTitle?.content}</Text>
                        <Text
                            variant='body2'
                            aria-label={`${selectedItems?.length} of ${maxSelectedPackages}`}
                            sx={{
                                ...styles.titleText,
                                [DtvTheme.breakpoints.up(DtvTheme.breakpoints.values.sm)]: {
                                    marginLeft: '25px'
                                }
                            }}
                        >{selectedItems?.length} of {maxSelectedPackages}</Text>
                    </DtvBox>
                    <DtvBox
                        sx={{
                            ...styles.arrowContainer
                        }}

                    >
                        {selectedItems?.length > 0 ? <DtvBox
                            role="button"
                            tabIndex={0}
                            aria-label={toggleAttrs['aria-label'] ?? toggleName}
                            data-linkposition={toggleAttrs['data-linkposition']}
                            data-linkdestination={toggleAttrs['data-linkdestination']}
                            onClick={() => {
                                handleCollapsedTray();
                            }}
                            onKeyDown={event => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    fireToggleKeyboard();
                                    handleCollapsedTray();
                                }
                            }}
                        ><CardMedia
                            component="img"
                            src={"https://www.directv.com/dtvassets/global/icons/2022/arrow-down_lt-bg.svg"}
                            alt={collapsedTray ? "Collapse tray" : "Expand tray"}
                            sx={{
                                ...styles.arrowStyles
                            }}
                        /></DtvBox> : <DtvBox
                                role="button"
                                tabIndex={0}
                                aria-label={closeAttrs['aria-label'] ?? "Close compare packages tray"}
                                data-linkposition={closeAttrs['data-linkposition']}
                                data-linkdestination={closeAttrs['data-linkdestination']}
                                onClick={() => {
                                    selectedItems?.length === 0 && handleTray();
                                }}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        selectedItems?.length === 0 && fireLinkClickFromAttrs(closeAttrs);
                                        selectedItems?.length === 0 && handleTray();
                                    }
                                }}
                        ><CloseIcon
                            sx={{
                                ...styles.arrowStyles
                            }}
                        /></DtvBox>}

                    </DtvBox>
                </DtvBox>
                {/* {collapsedTray &&  */}
                <DtvBox id="compare-packages-chips-container" sx={{
                    ...styles.comparePackagesChipsContainer,
                    opacity: (trayOpen && collapsedTray) ? 1 : 0
                }}
                    // `inert` removes the collapsed subtree from BOTH the focus order and the
                    // a11y tree, fixing the axe aria-hidden-focus audit; opacity alone left the
                    // chip links focusable. (An interim fix used visibility:hidden for the same
                    // goal — inert supersedes it, so that visibility hack was dropped on rebase.)
                    // aria-hidden is kept as defense-in-depth and is redundant with inert by
                    // design; don't "tidy" either away or the audit regresses.
                    aria-hidden={chipsHidden}
                    {...(chipsHidden ? INERT_SLOT : {})}
                >
                    {isMaxSelected && <Alert
                        severity={alert?.[0]?.severity}
                        description={alert?.[0]?.description?.content}
                        oneRow={true}
                    />}

                    <DtvBox
                        sx={{
display: 'flex',
flexDirection: 'column',
gap: '5px'
}}
                    >
                        <Text
                            variant='body1'
                            aria-label={subHeading1?.content}
                            sx={{
                                ...styles.subHeading1TitleText
                            }}
                        >
                            {subHeading1?.content}
                        </Text>
                        <DtvBox
                            id="genre-packs-chips-container"
                            sx={{
                                ...styles.genrePacksChipsContainer
                            }}
                        >
                            {subHeading1?.chips && getCustomChips(subHeading1?.chips)}
                        </DtvBox>
                        {subHeading1?.displayPkgType === 'genre' && subHeading3?.content && getsubHeading3()}
                    </DtvBox>
                    <DtvBox id="compare-packages-signature-chips-container"
                        sx={{
display: 'flex',
flexDirection: 'column',
gap: '5px',
marginTop: '21px'
}}
                    >
                        <DtvBox id="compare-packages-signature-title-container"
                            sx={{
                                ...styles.signatureTitleContainer
                             }}
                             role="button"
                             aria-expanded={showSignaturePacks}
                             tabIndex={0}
                             onClick={() => setShowSignaturePacks(!showSignaturePacks)}
                             onKeyDown={event => {
                                 if (event.key === 'Enter' || event.key === ' ') {
                                     event.preventDefault();
                                     setShowSignaturePacks(!showSignaturePacks);
                                 }
                             }}
                        >
                            <Text
                                aria-label={subHeading2?.content}
                                variant='body1'
                                sx={{
                                    ...styles.subHeading2TitleText
                                 }}
                            >{subHeading2?.content}</Text>
                            <CardMedia
                                component="img"
                                src={"https://www.directv.com/dtvassets/global/icons/2022/arrow-down_lt-bg.svg"}
                                alt={`down arrow icon`}
                                sx={{
                                    ...styles.arrowStyles,
                                    [DtvTheme.breakpoints.up(DtvTheme.breakpoints.values.sm)]: {
                                        marginLeft: '25px'
                                    },
                                    transform: showSignaturePacks ?
                                    "rotate(180deg)" :
                                    "rotate(0deg)"
                                }}
                            />
                        </DtvBox>
                        {showSignaturePacks &&
                         <DtvBox id="compare-packages-signature-chips-list-container"
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                marginTop: '10px',
                                [DtvTheme.breakpoints.up(DtvTheme.breakpoints.values.sm)]: {
                                    gap: '7px 16px'
                                },
                                [DtvTheme.breakpoints.down(DtvTheme.breakpoints.values.sm)]: {
                                    gap: '7px 8px'
                                },
                                opacity: showSignaturePacks ? 1 : 0,
                                transform: showSignaturePacks ? "scaleY(1)" : "scaleY(0.95)",
                                transition: 'transform 0.3s ease-in'
                            }}
                            aria-hidden={!showSignaturePacks}
                        >
                        {subHeading2?.chips && getCustomChips(subHeading2?.chips)}
                        </DtvBox>
                    }
                    </DtvBox>
                    <DtvBox id="compare-package-cta-container"
                        sx={{
                            ...styles.ctaContainer,
                            justifyContent: 'end',
                            ...(showSignaturePacks && subHeading2?.displayPkgType === 'genre' && subHeading3?.content && {
                                justifyContent: 'space-between',
                                [DtvTheme.breakpoints.down(DtvTheme.breakpoints.values.sm)]: {
                                    flexDirection: 'column',
                                    alignItems: 'start'
                                }
                            })
                        }}
                    >
                        {showSignaturePacks && subHeading2?.displayPkgType === 'genre' && subHeading3?.content && getsubHeading3()}
                        <DtvBox
                            sx={{
                                display: 'flex',
                                justifyContent: 'end',
                                gap: '10px',
                                alignItems: 'center'
                            }}
                        >
                            <Button size="medium"
                                aria-label={cta1Attrs['aria-label']}
                                data-linkposition={cta1Attrs['data-linkposition']}
                                data-linkdestination={cta1Attrs['data-linkdestination']}
                                onClick={() => {
                                    handleCompareClick(cta1?.targetUrl);
                                }}
                                disabled={selectedItems?.length === 0}
                                sx={{
                                    ...styles.cta1Button
                                }}
                            >{cta1?.content}</Button>
                            <Button size="medium" buttontype='tertiary'
                                sx={{
                                    ...styles.cta2Button
                                }}
                                aria-label={cta2Attrs['aria-label']}
                                data-linkposition={cta2Attrs['data-linkposition']}
                                data-linkdestination={cta2Attrs['data-linkdestination']}
                                onClick={() => {
                                    handleClearAll();
                                }}
                            ><CloseIcon />{cta2?.content}</Button>
                        </DtvBox>
                    </DtvBox>
                </DtvBox>
                {/* } */}
            </DtvBox>
    )
  } catch (error) {
    return (
            <ErrorDisplay
                error={error}
                component="ComparePackageTrayModule Component error"
            />
    );
  }
}

export default ComparePackagesTrayModule;
